import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';

import Typography from 'components/ui/Typography';
import Icon from 'components/Icon';

import css from './Checkbox.module.scss';

interface Checkbox {
    text?: string;
    disabled?: boolean;
    background?: 'light' | 'dark';
    type?: 'default' | 'chip';
    shortcutKey?: string;
}

const Checkbox: React.FC<Checkbox & JSX.IntrinsicElements['input']> = ({
    text,
    disabled,
    background = 'light',
    type = 'default',
    shortcutKey,
    ...rest
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key.toLowerCase() === shortcutKey?.toLowerCase()) {
            inputRef.current?.click();
            event.stopPropagation();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <label className={clsx(css.container, disabled && css.disabled)}>
            <input ref={inputRef} className={css.input} type="checkbox" {...rest} />
            {type === 'default' ? (
                <div className={clsx(css.indicator, css[`indicator_${background}`])}>
                    <Icon name="check-pointed-small" width={20} />
                </div>
            ) : (
                <div className={css.chip}>
                    <Typography color="inherit">
                        {text}
                        {shortcutKey && ` (${shortcutKey})`}
                    </Typography>
                </div>
            )}
            {type === 'default' && <Typography color="inherit">{text}</Typography>}
        </label>
    );
};

export default React.memo(Checkbox);
